import * as types from '../mutation-types';
import ConversationAPI from '../../api/conversations';
import {
  createLabelRequestGate,
  normalizeLabelTitles,
  updatingLabelTitles,
} from 'dashboard/helper/labelRequestGate';

const state = {
  records: {},
  pending: {},
  confirmedUpdatedAt: {},
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isError: false,
  },
};

const labelUpdateGate = createLabelRequestGate();
const labelRollbacks = new Map();

const hasOwn = (collection, id) =>
  Object.prototype.hasOwnProperty.call(collection, Number(id));

const activeTitles = (records, pending, id) => {
  const numericId = Number(id);
  if (hasOwn(pending, numericId)) return pending[numericId];
  return records[numericId] || [];
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getConversationLabels: $state => id => {
    return $state.records[Number(id)] || [];
  },
  getPendingLabels: $state => id => {
    const numericId = Number(id);
    if (!hasOwn($state.pending, numericId)) return null;
    return $state.pending[numericId];
  },
  hasSavedLabels: $state => id => hasOwn($state.records, Number(id)),
  getUpdatingLabels: $state => id => {
    const numericId = Number(id);
    return updatingLabelTitles(
      $state.records[numericId],
      hasOwn($state.pending, numericId) ? $state.pending[numericId] : null
    );
  },
  getActiveLabelTitles: $state => id =>
    activeTitles($state.records, $state.pending, id),
  shouldIgnoreIncomingLabels: $state => (id, updatedAt) => {
    const numericId = Number(id);
    if (hasOwn($state.pending, numericId)) return true;

    const confirmed = $state.confirmedUpdatedAt[numericId];
    if (confirmed == null || updatedAt == null || updatedAt === '') {
      return false;
    }

    return Number(updatedAt) < Number(confirmed);
  },
};

const rememberRollback = (id, stateSnapshot, rootGetters) => {
  if (labelRollbacks.has(id)) return;

  if (hasOwn(stateSnapshot.records, id)) {
    labelRollbacks.set(id, stateSnapshot.records[id]);
    return;
  }

  const chatLabels = rootGetters?.getConversationById?.(id)?.labels;
  labelRollbacks.set(id, Array.isArray(chatLabels) ? chatLabels : []);
};

export const actions = {
  get: async ({ commit }, conversationId) => {
    commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
      isFetching: true,
    });
    try {
      const response = await ConversationAPI.getLabels(conversationId);
      commit(types.default.SET_CONVERSATION_LABELS, {
        id: conversationId,
        data: response.data.payload,
      });
      commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
        isFetching: false,
      });
    } catch (error) {
      commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  update: async (
    { commit, state: stateSnapshot, rootGetters },
    { conversationId, labels }
  ) => {
    const id = Number(conversationId);
    const normalized = normalizeLabelTitles(labels);
    rememberRollback(id, stateSnapshot, rootGetters);

    commit(types.default.SET_CONVERSATION_LABELS_PENDING, {
      id,
      data: normalized,
    });
    commit(
      types.default.ASSIGN_CONVERSATION_LABELS,
      { id, labels: normalized },
      { root: true }
    );

    return labelUpdateGate.run(
      id,
      normalized,
      async (requestLabels, isLatest) => {
        commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
          isUpdating: true,
          isError: false,
        });
        try {
          const response = await ConversationAPI.updateLabels(
            id,
            requestLabels
          );
          if (!isLatest()) return response.data?.payload;

          const payload = Array.isArray(response.data?.payload)
            ? response.data.payload
            : requestLabels;
          commit(types.default.SET_CONVERSATION_LABELS, {
            id,
            data: payload,
          });
          if (response.data?.updated_at != null) {
            commit(types.default.SET_CONVERSATION_LABELS_CONFIRMED_AT, {
              id,
              updatedAt: response.data.updated_at,
            });
          }
          commit(types.default.SET_CONVERSATION_LABELS_PENDING, {
            id,
            data: null,
          });
          commit(
            types.default.ASSIGN_CONVERSATION_LABELS,
            { id, labels: payload },
            { root: true }
          );
          labelRollbacks.delete(id);
          commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
            isUpdating: false,
            isError: false,
          });
          return payload;
        } catch (error) {
          if (isLatest()) {
            const restore =
              labelRollbacks.get(id) || stateSnapshot.records[id] || [];
            labelRollbacks.delete(id);
            commit(types.default.SET_CONVERSATION_LABELS_PENDING, {
              id,
              data: null,
            });
            commit(
              types.default.ASSIGN_CONVERSATION_LABELS,
              { id, labels: restore },
              { root: true }
            );
            commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
              isUpdating: false,
              isError: true,
            });
          }
          return undefined;
        }
      }
    );
  },
  setBulkConversationLabels({ commit }, conversations) {
    commit(types.default.SET_BULK_CONVERSATION_LABELS, conversations);
  },
  setConversationLabel(
    { commit, state: stateSnapshot = {} },
    { id, data, updatedAt }
  ) {
    const numericId = Number(id);
    const pending = stateSnapshot.pending || {};
    const confirmedUpdatedAt = stateSnapshot.confirmedUpdatedAt || {};
    if (hasOwn(pending, numericId)) return;

    const confirmed = confirmedUpdatedAt[numericId];
    if (
      confirmed != null &&
      updatedAt != null &&
      updatedAt !== '' &&
      Number(updatedAt) < Number(confirmed)
    ) {
      return;
    }

    commit(types.default.SET_CONVERSATION_LABELS, { id: numericId, data });
  },
};

export const mutations = {
  [types.default.SET_CONVERSATION_LABELS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONVERSATION_LABELS]: ($state, { id, data }) => {
    $state.records = { ...$state.records, [Number(id)]: data };
  },
  [types.default.SET_CONVERSATION_LABELS_PENDING]: ($state, { id, data }) => {
    const pending = { ...$state.pending };
    if (data == null) delete pending[Number(id)];
    else pending[Number(id)] = data;
    $state.pending = pending;
  },
  [types.default.SET_CONVERSATION_LABELS_CONFIRMED_AT]: (
    $state,
    { id, updatedAt }
  ) => {
    $state.confirmedUpdatedAt = {
      ...$state.confirmedUpdatedAt,
      [Number(id)]: Number(updatedAt),
    };
  },
  [types.default.SET_BULK_CONVERSATION_LABELS]: ($state, conversations) => {
    const pending = $state.pending || {};
    const confirmedUpdatedAt = $state.confirmedUpdatedAt || {};
    const updatedRecords = { ...$state.records };
    conversations.forEach(conversation => {
      const id = Number(conversation.id);
      if (hasOwn(pending, id)) return;

      const confirmed = confirmedUpdatedAt[id];
      const incomingAt = conversation.updated_at;
      if (
        confirmed != null &&
        incomingAt != null &&
        incomingAt !== '' &&
        Number(incomingAt) < Number(confirmed)
      ) {
        return;
      }

      updatedRecords[id] = conversation.labels;
    });

    $state.records = updatedRecords;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
